import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    Lock,
    LogOut,
    Mail,
    Briefcase,
    Building2,
    RefreshCw,
    ArrowLeft,
    Inbox,
    Search,
    Send,
    CheckCheck,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "hh_admin_token";

const fmt = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const ReplyComposer = ({ submission, token, onSent }) => {
    const defaultSubject = `Re: ${submission.role_interest || "Your message"}`;
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        setSubject(`Re: ${submission.role_interest || "Your message"}`);
        setBody("");
    }, [submission.id, submission.role_interest]);

    const send = async (e) => {
        e.preventDefault();
        if (body.trim().length < 2) {
            toast.error("Write a longer reply first.");
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(
                `${API}/contact/${submission.id}/reply`,
                { subject: subject.trim(), body: body.trim() },
                { headers: { "X-Admin-Token": token } }
            );
            toast.success("Reply sent.");
            setBody("");
            onSent(res.data);
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "Reply failed to send.";
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    const replies = submission.replies || [];

    return (
        <div className="mt-10" data-testid="reply-section">
            {replies.length > 0 && (
                <div className="mb-10">
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                        Thread · {replies.length} repl{replies.length === 1 ? "y" : "ies"}
                    </p>
                    <ul className="mt-4 space-y-3">
                        {replies.map((r) => (
                            <li
                                key={r.id}
                                data-testid={`thread-reply-${r.id}`}
                                className="bg-bone-soft border border-line p-4"
                            >
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <p className="font-serif text-lg text-ink">
                                        {r.subject}
                                    </p>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft inline-flex items-center gap-1.5">
                                        {r.delivery_status >= 200 && r.delivery_status < 300 ? (
                                            <CheckCheck size={11} className="text-sienna" />
                                        ) : (
                                            <AlertCircle size={11} className="text-red-600" />
                                        )}
                                        {fmt(r.sent_at)}
                                    </span>
                                </div>
                                <p className="mt-2 font-sans text-sm text-ink-soft leading-relaxed whitespace-pre-wrap">
                                    {r.body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <form onSubmit={send} className="space-y-4" data-testid="reply-composer">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                    Compose reply
                </p>
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft">
                        Subject
                    </label>
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        data-testid="reply-subject"
                        className="w-full bg-transparent border-b border-line py-2 mt-1 font-sans text-base text-ink focus:outline-none focus:border-ink transition-colors"
                    />
                </div>
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-soft">
                        Message
                    </label>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={6}
                        placeholder="Write a short, direct reply…"
                        data-testid="reply-body"
                        className="w-full bg-bone-soft border border-line p-4 mt-1 font-sans text-base text-ink leading-relaxed resize-y focus:outline-none focus:border-ink transition-colors"
                    />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft">
                        Sends from work@hananhakim.com · replies route back to inbox
                    </p>
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="reply-send"
                        className="inline-flex items-center gap-2 bg-ink text-bone px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-sienna transition-colors disabled:opacity-50"
                    >
                        {sending ? "Sending…" : "Send reply"}
                        <Send size={13} />
                    </button>
                </div>
            </form>
        </div>
    );
};

const PasscodeGate = ({ onUnlock }) => {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setLoading(true);
        try {
            await axios.post(
                `${API}/admin/verify`,
                {},
                { headers: { "X-Admin-Token": code.trim() } }
            );
            localStorage.setItem(TOKEN_KEY, code.trim());
            toast.success("Welcome back, Hanan.");
            onUnlock(code.trim());
        } catch (err) {
            toast.error("Incorrect passcode.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-gate"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6"
        >
            <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
                onSubmit={submit}
                className="w-full max-w-md"
            >
                <Link
                    to="/"
                    data-testid="admin-back-link"
                    className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-bone/50 hover:text-bone transition-colors mb-10"
                >
                    <ArrowLeft size={13} /> Back to site
                </Link>
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-sienna">
                    Live Inquiry Inbox
                </p>
                <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
                    Enter <span className="italic text-sienna">admin</span>{" "}
                    passcode.
                </h1>
                <p className="font-sans text-base text-bone/60 leading-relaxed mt-5">
                    Unlock the real-time viewer of recruiter inquiries
                    submitted through the site.
                </p>

                <div className="mt-10">
                    <label className="font-mono text-[11px] uppercase tracking-[0.22em] text-bone/50 flex items-center gap-2">
                        <Lock size={12} /> Passcode
                    </label>
                    <input
                        type="password"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        autoFocus
                        data-testid="admin-passcode-input"
                        placeholder="••••••••"
                        className="w-full bg-transparent border-b border-line-strong py-3 mt-3 font-mono tracking-[0.3em] text-lg text-bone placeholder:text-bone/20 focus:outline-none focus:border-sienna transition-colors"
                    />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-unlock-button"
                    className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-sienna text-bone py-4 font-mono text-[12px] uppercase tracking-[0.22em] hover:bg-sienna-hover transition-colors disabled:opacity-50"
                >
                    {loading ? "Verifying…" : "Unlock inbox"}
                </button>

                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-bone/30 mt-6 text-center">
                    Private · Hanan Hakim · {new Date().getFullYear()}
                </p>
            </motion.form>
        </div>
    );
};

const InboxView = ({ token, onLogout }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/contact`, {
                headers: { "X-Admin-Token": token },
            });
            setItems(res.data || []);
            if (!selected && res.data?.length) setSelected(res.data[0]);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired. Please re-enter your passcode.");
                onLogout();
            } else {
                toast.error("Couldn't load inbox.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) =>
            [it.name, it.email, it.company, it.role_interest, it.message]
                .filter(Boolean)
                .some((v) => v.toLowerCase().includes(q))
        );
    }, [items, query]);

    return (
        <div
            data-testid="admin-inbox"
            className="min-h-screen bg-bone text-ink flex flex-col"
        >
            {/* Header */}
            <header className="border-b border-line">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <Link
                            to="/"
                            className="font-serif text-xl md:text-2xl tracking-tight"
                        >
                            Hanan{" "}
                            <span className="italic text-sienna">Hakim</span>
                        </Link>
                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft hidden md:inline">
                            / Inquiry Inbox
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={load}
                            data-testid="admin-refresh"
                            className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-ink hover:text-bone transition-colors"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-ink transition-colors"
                        >
                            <LogOut size={12} /> Lock
                        </button>
                    </div>
                </div>
            </header>

            {/* Stat strip */}
            <div className="border-b border-line bg-bone-soft">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-3 divide-x divide-line">
                    <div className="py-5 pr-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                            Total
                        </p>
                        <p
                            data-testid="admin-stat-total"
                            className="font-mono text-2xl md:text-3xl text-ink mt-1"
                        >
                            {items.length.toString().padStart(2, "0")}
                        </p>
                    </div>
                    <div className="py-5 px-4 md:px-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                            Last 7 days
                        </p>
                        <p className="font-mono text-2xl md:text-3xl text-ink mt-1">
                            {items
                                .filter((i) => {
                                    const d = new Date(i.created_at);
                                    return (
                                        Date.now() - d.getTime() <
                                        7 * 86400 * 1000
                                    );
                                })
                                .length.toString()
                                .padStart(2, "0")}
                        </p>
                    </div>
                    <div className="py-5 px-4 md:px-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
                            Unique companies
                        </p>
                        <p className="font-mono text-2xl md:text-3xl text-ink mt-1">
                            {new Set(
                                items.map((i) => i.company).filter(Boolean)
                            ).size
                                .toString()
                                .padStart(2, "0")}
                        </p>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 max-w-[1440px] w-full mx-auto px-6 md:px-12 lg:px-20 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* List */}
                <aside className="lg:col-span-5 xl:col-span-4 border-r-0 lg:border-r lg:border-line lg:pr-6">
                    <div className="flex items-center gap-3 border-b border-line pb-3">
                        <Search size={14} className="text-ink-soft" />
                        <input
                            placeholder="Search name, email, company…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="admin-search"
                            className="w-full bg-transparent font-sans text-sm placeholder:text-ink-soft/60 focus:outline-none"
                        />
                    </div>

                    {loading && (
                        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-8">
                            Loading…
                        </p>
                    )}

                    {!loading && filtered.length === 0 && (
                        <div className="mt-8 text-center">
                            <Inbox
                                size={28}
                                className="mx-auto text-ink-soft mb-3"
                            />
                            <p className="font-serif italic text-xl text-ink">
                                Inbox is quiet — for now.
                            </p>
                        </div>
                    )}

                    <ul
                        data-testid="admin-inbox-list"
                        className="mt-2 divide-y divide-line"
                    >
                        {filtered.map((it) => (
                            <li key={it.id}>
                                <button
                                    onClick={() => setSelected(it)}
                                    data-testid={`admin-inbox-item-${it.id}`}
                                    className={`w-full text-left py-5 transition-colors ${
                                        selected?.id === it.id
                                            ? "bg-bone-soft"
                                            : "hover:bg-bone-soft/60"
                                    }`}
                                >
                                    <div className="px-3 flex items-baseline justify-between gap-3">
                                        <p className="font-serif text-lg text-ink truncate">
                                            {it.name}
                                        </p>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft shrink-0">
                                            {fmt(it.created_at).split(",")[0]}
                                        </p>
                                    </div>
                                    <p className="px-3 font-mono text-[11px] uppercase tracking-[0.18em] text-sienna mt-1 truncate">
                                        {it.company || "—"}{" "}
                                        {it.role_interest
                                            ? `· ${it.role_interest}`
                                            : ""}
                                    </p>
                                    <p className="px-3 font-sans text-sm text-ink-soft mt-2 line-clamp-2 leading-relaxed">
                                        {it.message}
                                    </p>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Detail */}
                <section className="lg:col-span-7 xl:col-span-8 lg:pl-4">
                    {selected ? (
                        <article data-testid="admin-detail-panel">
                            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sienna">
                                Inquiry · {fmt(selected.created_at)}
                            </p>
                            <h2 className="font-serif text-4xl md:text-5xl text-ink mt-3 leading-[1.05]">
                                {selected.name}
                            </h2>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 border-y border-line py-5">
                                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em]">
                                    <Mail
                                        size={12}
                                        className="text-sienna shrink-0"
                                    />
                                    <a
                                        href={`mailto:${selected.email}`}
                                        className="text-ink hover:text-sienna transition-colors truncate"
                                    >
                                        {selected.email}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                                    <Building2
                                        size={12}
                                        className="text-sienna shrink-0"
                                    />
                                    <span className="truncate">
                                        {selected.company || "No company"}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft">
                                    <Briefcase
                                        size={12}
                                        className="text-sienna shrink-0"
                                    />
                                    <span className="truncate">
                                        {selected.role_interest || "No role specified"}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-8">
                                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-soft">
                                    Message
                                </p>
                                <div className="mt-4 bg-bone-soft border-l-2 border-sienna pl-5 pr-4 py-5">
                                    <p className="font-serif text-xl md:text-2xl text-ink leading-[1.55] whitespace-pre-wrap">
                                        {selected.message}
                                    </p>
                                </div>
                            </div>

                            <ReplyComposer
                                submission={selected}
                                token={token}
                                onSent={(reply) => {
                                    // Append to current selected + refresh list
                                    const updated = {
                                        ...selected,
                                        replies: [...(selected.replies || []), reply],
                                    };
                                    setSelected(updated);
                                    setItems((prev) =>
                                        prev.map((it) =>
                                            it.id === updated.id ? updated : it
                                        )
                                    );
                                }}
                            />
                        </article>
                    ) : !loading ? (
                        <div className="h-full flex items-center justify-center text-center py-20">
                            <div>
                                <Inbox
                                    size={40}
                                    className="mx-auto text-ink-soft mb-4"
                                />
                                <p className="font-serif italic text-2xl text-ink">
                                    Select a message to read.
                                </p>
                            </div>
                        </div>
                    ) : null}
                </section>
            </div>
        </div>
    );
};

export default function AdminInbox() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    useEffect(() => {
        document.title = "Inbox — Hanan Hakim";
    }, []);

    const handleLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast("Locked.", { description: "Session ended." });
    };

    if (!token) return <PasscodeGate onUnlock={setToken} />;
    return <InboxView token={token} onLogout={handleLogout} />;
}
